import { memo, useCallback, useEffect, useMemo } from "react";

import { Checkbox, Button, Row } from "@narmi/design_system";
import { modules } from "byzantine";

import styles from "./Styles.module.scss";

import type { CommonProps } from "./types";

type Props = CommonProps & {
  question: API.NPS.NpsScoreQuestion;
  isFinalQuestion: boolean;
};

const NpsScoreQuestion = ({
  question,
  isFinalQuestion,
  setMode,
  close,
  setFooter,
}: Props) => {
  const { loading, submit } = modules.nps.useSubmitAnswers(close);
  const { choices, buttonDisabled } = modules.nps.useNpsScoreQuestion(question);
  const buttonLabels = modules.nps.useButtonLabels();

  const onSubmit = useCallback(() => {
    if (!isFinalQuestion) {
      setMode("freeformtext");
    } else {
      submit();
    }
  }, [isFinalQuestion, setMode, submit]);

  const footer = useMemo(() => {
    return (
      <Row className="margin--top--l" justifyContent="end">
        <Row.Item shrink>
          <Button
            onClick={onSubmit}
            isLoading={loading}
            disabled={buttonDisabled}
            label={!isFinalQuestion ? buttonLabels.next : buttonLabels.submit}
          />
        </Row.Item>
      </Row>
    );
  }, [
    buttonDisabled,
    buttonLabels.next,
    buttonLabels.submit,
    isFinalQuestion,
    loading,
    onSubmit,
  ]);

  useEffect(() => {
    setFooter(footer);
  }, [footer, setFooter]);

  return (
    <>
      <div className="margin--y--m">{question.text}</div>{" "}
      <div className={styles.npsScoreContainer}>
        {choices.map(({ choice, selected, onSelect }, i) => {
          return (
            <div className={styles.npsScoreItem} key={`nps_score-${i}`}>
              <Checkbox
                name={`nps_score-${i}`}
                value={`${choice}`}
                checked={selected}
                onChange={onSelect}
              />
              <div className={"padding--x--xxs padding--y--xs"}>{choice}</div>
            </div>
          );
        })}
      </div>
      <Row className={styles.likelyText}>
        <Row.Item>{buttonLabels.leastLikely}</Row.Item>
        <Row.Item className="textAlign--right" shrink>
          {buttonLabels.mostLikely}
        </Row.Item>
      </Row>
    </>
  );
};

export default memo(NpsScoreQuestion);
